import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppState } from "../../../store/root.reducer";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import {
  GetEdmDataset,
  GetEdmDatasetBlocked,
  GetEdmDatasetHandoffPackage,
  GetEdmDatasetSummary,
  RecomputeEdmDataset,
  UpdateEdmDataset,
  ValidateEdmDatasetMedia,
} from "./store/admin-edm.actions";
import { AdminEdmDeliveryProfile, AdminEdmEditableDeliveryProfile } from "../../../types/adminEdm";
import {
  buildEditableDeliveryProfiles,
  COMMON_LANGUAGE_TAG_OPTIONS,
  formatBlockerCode,
  formatValidationIssueCode,
  haveEditableDeliveryProfilesChanged,
  isValidLanguageTag,
  LANGUAGE_TAG_HELP_TEXT,
} from "./edm.helpers";
import {
  getSafeHttpUrl,
  normalizeAdminEdmBlockedRecords,
  normalizeAdminEdmDatasetDetail,
  normalizeAdminEdmDatasetMediaValidation,
  normalizeAdminEdmDatasetSummary,
  normalizeAdminEdmHandoffPackage,
} from "./admin-edm.normalizers";
import { isDatasetNotFoundMessage } from "./admin-edm.errors";
import { getInstitutionsRequest } from "../store/admin.api";
import { Institution } from "../../../models/institution.model";
import adminTheme from "../admin-theme.module.css";
import workspaceTheme from "../../../components/artwork-workspace/workspaceTheme.module.css";

type DatasetFormState = {
  displayName: string;
  institutionId: string;
  enabled: boolean;
  defaultLanguage: string;
  summary: string;
  deliveryProfiles: AdminEdmEditableDeliveryProfile[];
};

const buildDatasetFormState = (activeDataset: {
  displayName?: string | null;
  institutionId?: string | null;
  enabled: boolean;
  defaultLanguage?: string | null;
  summary?: string | null;
  deliveryProfiles: AdminEdmDeliveryProfile[];
}): DatasetFormState => ({
  displayName: activeDataset.displayName || "",
  institutionId: activeDataset.institutionId || "",
  enabled: activeDataset.enabled,
  defaultLanguage: activeDataset.defaultLanguage || "",
  summary: activeDataset.summary || "",
  deliveryProfiles: buildEditableDeliveryProfiles(activeDataset.deliveryProfiles),
});

const toInstitutionItems = (response: any): Institution[] => {
  const items = response?.data?.data?.items;
  return Array.isArray(items) ? items : [];
};

const downloadJsonFile = (filename: string, payload: unknown) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

const buildDatasetDownloadName = (displayName?: string | null): string => {
  const normalized = String(displayName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || "dataset";
};

const renderExternalLink = (label: string, href: string | null) => {
  const safeHref = getSafeHttpUrl(href);
  return safeHref
    ? <a className={workspaceTheme.workspaceLinkAction} href={safeHref} target="_blank" rel="noreferrer">{label}</a>
    : <span className="text-white-50">{label}: unavailable</span>;
};

export const AdminEdmDatasetDetailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const datasetKey = params.datasetKey || "";
  const {
    activeDataset: rawActiveDataset,
    datasetSummary: rawDatasetSummary,
    datasetBlockedRecords: rawDatasetBlockedRecords,
    datasetValidation: rawDatasetValidation,
    handoffPackage: rawHandoffPackage,
    loadingDatasetMeta,
    loadingDatasetSummary,
    loadingDatasetBlocked,
    loadingHandoffPackage,
    savingDatasetProfile,
    runningDatasetRecompute,
    runningDatasetValidation,
    datasetMetaError,
    datasetSummaryError,
    datasetBlockedError,
    datasetValidationError,
    handoffPackageError,
  } = useSelector((state: AppState) => state.adminEdm);
  const activeDataset = React.useMemo(
    () => normalizeAdminEdmDatasetDetail(rawActiveDataset),
    [rawActiveDataset]
  );
  const datasetSummary = React.useMemo(
    () => normalizeAdminEdmDatasetSummary(rawDatasetSummary),
    [rawDatasetSummary]
  );
  const datasetBlockedRecords = React.useMemo(
    () => normalizeAdminEdmBlockedRecords(rawDatasetBlockedRecords),
    [rawDatasetBlockedRecords]
  );
  const datasetValidation = React.useMemo(
    () => normalizeAdminEdmDatasetMediaValidation(rawDatasetValidation),
    [rawDatasetValidation]
  );
  const handoffPackage = React.useMemo(
    () => normalizeAdminEdmHandoffPackage(rawHandoffPackage),
    [rawHandoffPackage]
  );

  const [formState, setFormState] = React.useState<DatasetFormState | null>(null);
  const [institutions, setInstitutions] = React.useState<Institution[]>([]);
  const [showAdvancedProfiles, setShowAdvancedProfiles] = React.useState(false);
  const [showPrimaryProfile, setShowPrimaryProfile] = React.useState(false);
  const hydratedFormKeyRef = React.useRef<string | null>(null);
  const datasetMetaResolved = Boolean(activeDataset && activeDataset.datasetKey === datasetKey);
  const datasetMetaNotFound = Boolean(
    datasetMetaError &&
    !loadingDatasetMeta &&
    !datasetMetaResolved &&
    isDatasetNotFoundMessage(datasetMetaError)
  );
  const datasetMetaLoadFailed = Boolean(
    datasetMetaError &&
    !loadingDatasetMeta &&
    !datasetMetaResolved &&
    !datasetMetaNotFound
  );
  const datasetFormHydrated = Boolean(formState);
  const handoffWarnings = handoffPackage?.warnings || [];
  const datasetCapabilities = activeDataset?.capabilities || {
    canEditMetadata: true,
    canEditInstitution: true,
    canEditDeliveryProfiles: true,
    canGenerateHandoff: true,
    canToggleEnabled: true,
    canDelete: true,
  };

  React.useEffect(() => {
    if (!datasetKey) return;
    dispatch(GetEdmDataset({ datasetKey }));
  }, [datasetKey, dispatch]);

  React.useEffect(() => {
    if (!datasetMetaResolved) return;
    dispatch(GetEdmDatasetSummary({ datasetKey }));
    dispatch(GetEdmDatasetBlocked({ datasetKey }));
  }, [datasetMetaResolved, datasetKey, dispatch]);

  React.useEffect(() => {
    if (!datasetMetaResolved || !datasetCapabilities.canEditInstitution) {
      setInstitutions([]);
      return;
    }

    let alive = true;
    getInstitutionsRequest()
      .then((response) => {
        if (!alive) return;
        setInstitutions(toInstitutionItems(response));
      })
      .catch(() => {
        if (!alive) return;
        setInstitutions([]);
      });

    return () => {
      alive = false;
    };
  }, [datasetCapabilities.canEditInstitution, datasetMetaResolved]);

  const nextFormState = React.useMemo(
    () => (activeDataset ? buildDatasetFormState(activeDataset) : null),
    [activeDataset]
  );
  const nextFormStateSignature = React.useMemo(
    () => (nextFormState ? JSON.stringify(nextFormState) : null),
    [nextFormState]
  );
  const nextFormStateKey = React.useMemo(
    () => (nextFormStateSignature ? `${datasetKey}:${nextFormStateSignature}` : null),
    [datasetKey, nextFormStateSignature]
  );

  React.useEffect(() => {
    if (!nextFormState || !nextFormStateKey) return;
    if (hydratedFormKeyRef.current === nextFormStateKey) return;
    hydratedFormKeyRef.current = nextFormStateKey;
    setFormState(nextFormState);
  }, [nextFormState, nextFormStateKey]);

  const readAggregatorProfile = activeDataset?.deliveryProfiles?.find((profile: AdminEdmDeliveryProfile) => profile.profileId === "aggregator-default")
    || activeDataset?.deliveryProfiles?.[0];
  const aggregatorProfile = formState?.deliveryProfiles?.find((profile: AdminEdmEditableDeliveryProfile) => profile.profileId === "aggregator-default")
    || formState?.deliveryProfiles?.[0];
  const advancedProfiles = (activeDataset?.deliveryProfiles || []).filter((profile: AdminEdmDeliveryProfile) => profile.profileId !== "aggregator-default");
  const defaultLanguageValid = isValidLanguageTag(formState?.defaultLanguage);
  const metadataReadOnly = !datasetCapabilities.canEditMetadata;
  const deliveryProfilesReadOnly = !datasetCapabilities.canEditDeliveryProfiles;
  const handoffRecords = handoffPackage?.exportableRecords || [];
  const handoffRecordCount = handoffPackage?.exportableRecordCount ?? handoffRecords.length;
  const institutionOptions = React.useMemo(() => {
    const options = institutions.filter((institution) => institution.active !== false);
    const currentInstitution = activeDataset?.institution;
    if (
      currentInstitution?._id &&
      !options.some((institution) => institution._id === currentInstitution._id)
    ) {
      return [
        {
          _id: currentInstitution._id,
          name: currentInstitution.name || "Inactive institution",
          slug: currentInstitution.slug || "",
          edmProviderName: currentInstitution.edmProviderName || "",
          sketchfabImportLimit: currentInstitution.sketchfabImportLimit || 10,
          active: currentInstitution.active !== false,
        },
        ...options,
      ];
    }
    return options;
  }, [activeDataset?.institution, institutions]);
  const selectedInstitution = React.useMemo(
    () =>
      institutionOptions.find((institution) => institution._id === formState?.institutionId) ||
      activeDataset?.institution ||
      null,
    [activeDataset?.institution, formState?.institutionId, institutionOptions]
  );
  const providerDisplayValue =
    selectedInstitution?.edmProviderName ||
    activeDataset?.institution?.edmProviderName ||
    readAggregatorProfile?.provider ||
    "";

  const updateAggregatorProfile = (patch: Partial<AdminEdmEditableDeliveryProfile>) => {
    if (!aggregatorProfile || deliveryProfilesReadOnly) return;
    setFormState((current) => current ? ({
      ...current,
      deliveryProfiles: current.deliveryProfiles.map((profile: AdminEdmEditableDeliveryProfile) =>
        profile.profileId === aggregatorProfile.profileId ? { ...profile, ...patch } : profile
      ),
    }) : current);
  };

  const onSave = () => {
    if (!defaultLanguageValid) return;
    const payload: Record<string, unknown> = {
      displayName: formState.displayName || null,
      defaultLanguage: formState.defaultLanguage || null,
      summary: formState.summary || null,
    };
    if (datasetCapabilities.canEditInstitution) {
      payload.institutionId = formState.institutionId || null;
    }
    if (datasetCapabilities.canToggleEnabled) {
      payload.enabled = formState.institutionId ? formState.enabled : false;
    }
    if (
      datasetCapabilities.canEditDeliveryProfiles &&
      haveEditableDeliveryProfilesChanged(activeDataset.deliveryProfiles, formState.deliveryProfiles)
    ) {
      payload.deliveryProfiles = buildEditableDeliveryProfiles(formState.deliveryProfiles);
    }
    dispatch(UpdateEdmDataset({
      datasetKey,
      payload,
      onSuccess: () => {
        dispatch(GetEdmDatasetSummary({ datasetKey }));
        dispatch(GetEdmDatasetBlocked({ datasetKey }));
      },
    }));
  };

  const onRecompute = () => {
    dispatch(RecomputeEdmDataset({
      datasetKey,
      payload: {},
      onSuccess: () => {
        dispatch(GetEdmDatasetSummary({ datasetKey }));
        dispatch(GetEdmDatasetBlocked({ datasetKey }));
      },
    }));
  };

  const onValidateMedia = () => {
    dispatch(ValidateEdmDatasetMedia({ datasetKey, payload: {} }));
  };

  const onGenerateHandoffPackage = () => {
    dispatch(GetEdmDatasetHandoffPackage({ datasetKey }));
  };

  if (loadingDatasetMeta && !activeDataset) {
    return (
      <div className="container py-4 text-white">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading dataset...
      </div>
    );
  }

  if (datasetMetaNotFound) {
    return <div className="container py-4 text-white">Dataset not found.</div>;
  }

  if (datasetMetaLoadFailed) {
    return (
      <div className="container py-4 text-white">
        <Alert variant="danger" className="mb-0">{datasetMetaError}</Alert>
      </div>
    );
  }

  if (!datasetMetaResolved) {
    return (
      <div className="container py-4 text-white">
        <Spinner animation="border" size="sm" className="me-2" />
        Initializing dataset...
      </div>
    );
  }

  if (!datasetFormHydrated || !formState || !activeDataset) {
    return (
      <div className="container py-4 text-white">
        <Spinner animation="border" size="sm" className="me-2" />
        Hydrating dataset editor...
      </div>
    );
  }

  const visibleDatasetName = activeDataset.displayName || "Untitled dataset";

  return (
    <div className="container py-4 text-white">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1">{visibleDatasetName}</h2>
          <div className="d-flex gap-2 flex-wrap mb-2">
            {activeDataset.institution?.name ? <Badge bg="primary">{activeDataset.institution.name}</Badge> : null}
            {!activeDataset.institution?.name ? <Badge bg="secondary">Unassigned</Badge> : null}
            {activeDataset.enabled ? <Badge bg="success">Enabled</Badge> : <Badge bg="secondary">Disabled</Badge>}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
            onClick={onValidateMedia}
            disabled={runningDatasetValidation}
          >
            {runningDatasetValidation ? "Validating..." : "Validate Dataset Media"}
          </Button>
          {datasetCapabilities.canGenerateHandoff ? (
            <Button
              variant="light"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
              onClick={onGenerateHandoffPackage}
              disabled={loadingHandoffPackage}
            >
              {loadingHandoffPackage ? "Generating..." : "Generate Handoff Package"}
            </Button>
          ) : null}
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
            onClick={onRecompute}
            disabled={runningDatasetRecompute}
          >
            {runningDatasetRecompute ? "Recomputing..." : "Recompute Dataset Readiness"}
          </Button>
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
            onClick={onSave}
            disabled={metadataReadOnly || savingDatasetProfile || !defaultLanguageValid}
          >
            {savingDatasetProfile ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {datasetMetaError && !datasetMetaNotFound ? <Alert variant="danger">{datasetMetaError}</Alert> : null}
      {datasetSummaryError ? <Alert variant="warning">{datasetSummaryError}</Alert> : null}
      {datasetBlockedError ? <Alert variant="warning">{datasetBlockedError}</Alert> : null}
      {datasetValidationError ? <Alert variant="warning">{datasetValidationError}</Alert> : null}
      {datasetCapabilities.canGenerateHandoff && handoffPackageError ? <Alert variant="warning">{handoffPackageError}</Alert> : null}

      <Row className="g-3 mb-4">
        <Col md={3}><Card bg="dark" text="light"><Card.Body><div className="text-white-50 small">EDM Valid</div><div className="fs-3 fw-semibold">{loadingDatasetSummary ? "..." : (datasetSummary?.edmValidCount ?? 0)}</div></Card.Body></Card></Col>
        <Col md={3}><Card bg="dark" text="light"><Card.Body><div className="text-white-50 small">Publishable A/1</div><div className="fs-3 fw-semibold">{loadingDatasetSummary ? "..." : (datasetSummary?.publishableA1 ?? 0)}</div></Card.Body></Card></Col>
        <Col md={3}><Card bg="dark" text="light"><Card.Body><div className="text-white-50 small">Tier-capable B/2</div><div className="fs-3 fw-semibold">{loadingDatasetSummary ? "..." : (datasetSummary?.tierCapableB2 ?? 0)}</div></Card.Body></Card></Col>
        <Col md={3}><Card bg="dark" text="light"><Card.Body><div className="text-white-50 small">Blocked Records</div><div className="fs-3 fw-semibold">{loadingDatasetBlocked ? "..." : datasetBlockedRecords.length}</div></Card.Body></Card></Col>
      </Row>

      <Row className="g-4">
        <Col lg={5}>
          <Card bg="dark" text="light" className="mb-4">
            <Card.Header>Dataset Profile</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Display name</Form.Label>
                <Form.Control
                  value={formState.displayName}
                  disabled={metadataReadOnly}
                  onChange={(event) => setFormState((current) => (current ? { ...current, displayName: event.target.value } : current))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Institution</Form.Label>
                {datasetCapabilities.canEditInstitution ? (
                  <Form.Select
                    className={workspaceTheme.workspaceSelect}
                    value={formState.institutionId}
                    onChange={(event) => setFormState((current) => current ? ({
                      ...current,
                      institutionId: event.target.value,
                      enabled: event.target.value ? current.enabled : false,
                    }) : current)}
                  >
                    <option value="">Unassigned</option>
                    {institutionOptions.map((institution) => (
                      <option key={institution._id} value={institution._id}>
                        {institution.active === false ? `${institution.name} (inactive)` : institution.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    value={activeDataset.institution?.name || "Unassigned"}
                    readOnly
                    className={`${adminTheme.darkReadOnlyField} ${adminTheme.darkReadOnlyFieldLocked}`}
                  />
                )}
              </Form.Group>
              <div className="mb-3">
                <div className="text-white-50 small">Assigned artworks</div>
                <div>{String(activeDataset.assignedArtworkCount || 0)}</div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="dataset-default-language">Default language tag</Form.Label>
                <Form.Control
                  id="dataset-default-language"
                  list="dataset-language-tag-options"
                  value={formState.defaultLanguage}
                  disabled={metadataReadOnly}
                  isInvalid={!defaultLanguageValid}
                  placeholder="en"
                  onChange={(event) => setFormState((current) => (current ? { ...current, defaultLanguage: event.target.value } : current))}
                />
                <datalist id="dataset-language-tag-options">
                  {COMMON_LANGUAGE_TAG_OPTIONS.map((languageTag) => (
                    <option key={languageTag} value={languageTag} />
                  ))}
                </datalist>
                <Form.Text className="text-white-50">{LANGUAGE_TAG_HELP_TEXT}</Form.Text>
                <Form.Control.Feedback type="invalid">
                  Enter a valid BCP47 language tag such as en, ro, en-GB, or pt-BR.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Summary</Form.Label>
                <Form.Control
                  value={formState.summary}
                  disabled={metadataReadOnly}
                  onChange={(event) => setFormState((current) => (current ? { ...current, summary: event.target.value } : current))}
                />
              </Form.Group>
              {datasetCapabilities.canToggleEnabled ? (
                <Form.Check
                  id="dataset-enabled"
                  type="checkbox"
                  label="Enabled"
                  checked={formState.enabled}
                  disabled={!formState.institutionId}
                  onChange={(event) => setFormState((current) => (current ? { ...current, enabled: event.target.checked } : current))}
                />
              ) : (
                <div>
                  <div className="text-white-50 small">Status</div>
                  <div>{formState.enabled ? "Enabled" : "Disabled"}</div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card bg="dark" text="light">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Delivery Profile: aggregator-default</span>
              <Button
                variant="light"
                size="sm"
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                onClick={() => setShowPrimaryProfile((current) => !current)}
              >
                {showPrimaryProfile ? "Hide" : "Show"}
              </Button>
            </Card.Header>
            {showPrimaryProfile ? (
              <Card.Body className={deliveryProfilesReadOnly ? adminTheme.darkReadOnlySection : undefined}>
                {deliveryProfilesReadOnly ? (
                  <Alert variant="secondary" className="mb-3">
                    Delivery profiles are admin-managed. Curators can review them here but cannot change them.
                  </Alert>
                ) : null}
                {!aggregatorProfile ? (
                  <div className="text-white-50">No editable delivery profile is configured for this dataset.</div>
                ) : (
                  <Row className="g-3">
                  <Col md={6}>
                    <Form.Check
                      id="aggregator-enabled"
                      type="checkbox"
                      label="Enabled"
                      checked={Boolean(aggregatorProfile.enabled)}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ enabled: event.target.checked })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      id="aggregator-primary"
                      type="checkbox"
                      label="Primary"
                      checked={Boolean(aggregatorProfile.primary)}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ primary: event.target.checked })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Consumer name</Form.Label>
                    <Form.Control
                      value={aggregatorProfile.consumerName || ""}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ consumerName: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Public base URI</Form.Label>
                    <Form.Control
                      value={aggregatorProfile.publicBaseUri || ""}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ publicBaseUri: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Provider</Form.Label>
                    <Form.Control
                      value={providerDisplayValue}
                      readOnly
                      className={adminTheme.darkReadOnlyField}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Intermediate provider</Form.Label>
                    <Form.Control
                      value={aggregatorProfile.intermediateProvider || ""}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ intermediateProvider: event.target.value || null })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Minimum metadata tier</Form.Label>
                    <Form.Select
                      className={workspaceTheme.workspaceSelect}
                      value={aggregatorProfile.minimumMetadataTier || "A"}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ minimumMetadataTier: event.target.value as any })}
                    >
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Minimum content tier</Form.Label>
                    <Form.Select
                      className={workspaceTheme.workspaceSelect}
                      value={String(aggregatorProfile.minimumContentTier || 1)}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ minimumContentTier: Number(event.target.value) as any })}
                    >
                      {[1, 2, 3, 4].map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Publication gate</Form.Label>
                    <Form.Select
                      className={workspaceTheme.workspaceSelect}
                      value={aggregatorProfile.publicationGate || "published"}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ publicationGate: event.target.value as any })}
                    >
                      <option value="published">published</option>
                      <option value="ready-and-published">ready-and-published</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>OAI repository name</Form.Label>
                    <Form.Control
                      value={aggregatorProfile.oaiRepositoryName || ""}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ oaiRepositoryName: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>OAI admin email</Form.Label>
                    <Form.Control
                      value={aggregatorProfile.oaiAdminEmail || ""}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ oaiAdminEmail: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>OAI set name</Form.Label>
                    <Form.Control
                      value={aggregatorProfile.oaiSetName || ""}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ oaiSetName: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>OAI set description</Form.Label>
                    <Form.Control
                      value={aggregatorProfile.oaiSetDescription || ""}
                      disabled={deliveryProfilesReadOnly}
                      onChange={(event) => updateAggregatorProfile({ oaiSetDescription: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="text-white-50 small">Transport</div>
                    <div>{aggregatorProfile.transport || "oai"}</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-white-50 small">Profile type</div>
                    <div>{aggregatorProfile.type || "aggregator-harvest"}</div>
                  </Col>
                  </Row>
                )}
              </Card.Body>
            ) : null}
          </Card>

          <Card bg="dark" text="light" className="mt-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Advanced Delivery Profiles</span>
              <Button
                variant="light"
                size="sm"
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                onClick={() => setShowAdvancedProfiles((current) => !current)}
              >
                {showAdvancedProfiles ? "Hide" : "Show"}
              </Button>
            </Card.Header>
            {showAdvancedProfiles ? (
              <Card.Body>
                {advancedProfiles.length === 0 ? (
                  <div className="text-white-50">No additional delivery profiles are configured for this dataset.</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {advancedProfiles.map((profile: AdminEdmDeliveryProfile) => (
                      <div key={profile.profileId} className="border rounded p-3">
                        <div className="d-flex gap-2 flex-wrap mb-2">
                          <Badge bg="secondary">{profile.profileId}</Badge>
                          <Badge bg="dark">{profile.type}</Badge>
                          <Badge bg={profile.enabled ? "success" : "secondary"}>{profile.enabled ? "Enabled" : "Disabled"}</Badge>
                          {profile.primary ? <Badge bg="info">Primary</Badge> : null}
                        </div>
                        <Row className="g-2 small">
                          <Col md={6}><div className="text-white-50">Provider</div><div>{profile.provider || "N/A"}</div></Col>
                          <Col md={6}><div className="text-white-50">Intermediate Provider</div><div>{profile.intermediateProvider || "N/A"}</div></Col>
                          <Col md={6}><div className="text-white-50">Threshold</div><div>{profile.minimumMetadataTier}/{profile.minimumContentTier}</div></Col>
                          <Col md={6}><div className="text-white-50">Publication Gate</div><div>{profile.publicationGate}</div></Col>
                          <Col md={12}><div className="text-white-50">Public Base URI</div><div className="text-break">{profile.publicBaseUri || "N/A"}</div></Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            ) : null}
          </Card>
        </Col>

        <Col lg={7}>
          <Card bg="dark" text="light" className="mb-4">
            <Card.Header>Useful Links</Card.Header>
            <Card.Body className="d-flex flex-column gap-2">
              {renderExternalLink("Open OAI Identify", activeDataset.publicLinks.oaiIdentifyUrl)}
              {renderExternalLink("Open OAI ListSets", activeDataset.publicLinks.oaiListSetsUrl)}
              {activeDataset.publicLinks.oaiListIdentifiersUrl ? (
                renderExternalLink("Open OAI ListIdentifiers", activeDataset.publicLinks.oaiListIdentifiersUrl)
              ) : null}
              {renderExternalLink("Open dataset RDF", activeDataset.publicLinks.datasetRdfUrl)}
              {renderExternalLink("Open OAI ListRecords", activeDataset.publicLinks.oaiListRecordsUrl)}
            </Card.Body>
          </Card>

          <Card bg="dark" text="light" className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Dataset Media Validation</span>
              {datasetValidation ? <Badge bg={datasetValidation.failed === 0 ? "success" : "danger"}>{datasetValidation.failed === 0 ? "Pass" : "Issues"}</Badge> : null}
            </Card.Header>
            <Card.Body>
              {!datasetValidation ? (
                <div className="text-white-50">Run dataset validation before a handoff to verify landing pages, oEmbed, preview media, and model delivery.</div>
              ) : (
                <>
                  <div className="d-flex gap-2 flex-wrap mb-3">
                    <Badge bg="secondary">{datasetValidation.scanned} scanned</Badge>
                    <Badge bg="success">{datasetValidation.passed} passed</Badge>
                    <Badge bg={datasetValidation.failed === 0 ? "secondary" : "danger"}>{datasetValidation.failed} failed</Badge>
                    <Badge bg="warning">{datasetValidation.warningCount} warnings</Badge>
                  </div>
                  <div className="small text-white-50 mb-3">Checked at {new Date(datasetValidation.checkedAt).toLocaleString()}</div>
                  {datasetValidation.records.length === 0 ? (
                    <div className="text-white-50">No records were found in this dataset.</div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped hover variant="dark" className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Artwork</th>
                            <th>State</th>
                            <th>Status</th>
                            <th>First issue</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {datasetValidation.records.map((record) => (
                            <tr key={record.artworkId}>
                              <td className="font-monospace">{record.title || record.artworkId}</td>
                              <td><Badge bg="secondary">{record.recordState}</Badge></td>
                              <td>
                                <div className="d-flex gap-2 flex-wrap">
                                  <Badge bg={record.passed ? "success" : "danger"}>{record.passed ? "Pass" : "Fail"}</Badge>
                                  {record.warningCount ? <Badge bg="warning">{record.warningCount} warnings</Badge> : null}
                                </div>
                              </td>
                              <td>{record.firstIssue ? formatValidationIssueCode(record.firstIssue.code) : "No issues"}</td>
                              <td className="text-end">
                                <Button
                                  variant="light"
                                  size="sm"
                                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary} ${workspaceTheme.workspaceButtonSmall}`}
                                  onClick={() => navigate(`/admin/edm/artworks/${record.artworkId}`)}
                                >
                                  Open Artwork
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          {datasetCapabilities.canGenerateHandoff ? (
            <Card bg="dark" text="light" className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Aggregator Handoff Package</span>
                {handoffPackage ? (
                  <Button
                    variant="light"
                    size="sm"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                    onClick={() => downloadJsonFile(`${buildDatasetDownloadName(activeDataset.displayName)}-handoff-package.json`, handoffPackage)}
                  >
                    Download JSON
                  </Button>
                ) : null}
              </Card.Header>
              <Card.Body>
                {!handoffPackage ? (
                  <div className="text-white-50">Generate the handoff package to collect OAI, RDF, the full exportable record list, and rollout-target details for the first aggregator delivery.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {handoffWarnings.length > 0 ? (
                      <Alert variant="warning" className="mb-2">
                        {handoffWarnings.map((warning) => <div key={warning}>{warning}</div>)}
                      </Alert>
                    ) : null}
                    <div><span className="text-white-50">Release target:</span> {handoffPackage.releaseTarget}</div>
                    <div><span className="text-white-50">Viewer status:</span> {handoffPackage.viewerStatus}</div>
                    <div><span className="text-white-50">Generated:</span> {new Date(handoffPackage.generatedAt).toLocaleString()}</div>
                    <div><span className="text-white-50">Exportable records:</span> {handoffRecordCount}</div>
                    {renderExternalLink("Open OAI Identify", handoffPackage.oaiIdentifyUrl)}
                    {renderExternalLink("Open OAI ListSets", handoffPackage.oaiListSetsUrl)}
                    {renderExternalLink("Open OAI ListIdentifiers", handoffPackage.oaiListIdentifiersUrl)}
                    {renderExternalLink("Open OAI ListRecords", handoffPackage.oaiListRecordsUrl)}
                    {renderExternalLink("Open dataset RDF", handoffPackage.datasetRdfUrl)}
                    <div className="small text-white-50 mt-2">Exportable records</div>
                    {handoffRecords.map((record) => (
                      <div key={record.artworkId} className="border rounded p-2">
                        <div className="fw-semibold">{record.title || record.artworkId}</div>
                        <div>{renderExternalLink("RDF record", record.rdfRecordUrl)}</div>
                        <div>{renderExternalLink("Landing page", record.landingPageUrl)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          ) : null}

          <Card bg="dark" text="light">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Blocked Records</span>
              <Badge bg="secondary">{loadingDatasetBlocked ? "..." : datasetBlockedRecords.length}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table striped hover variant="dark" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Artwork</th>
                      <th>State</th>
                      <th>Tier</th>
                      <th>Blockers</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDatasetBlocked ? (
                      <tr><td colSpan={5} className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Loading blocked records...</td></tr>
                    ) : datasetBlockedRecords.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-4">No blocked records.</td></tr>
                    ) : datasetBlockedRecords.map((record) => {
                      const blockers = [
                        ...record.blockers.baseline,
                        ...record.blockers.metadataTier,
                        ...record.blockers.contentTier,
                      ];

                      return (
                        <tr key={record.artworkId}>
                          <td className="font-monospace">{record.title || record.artworkId}</td>
                          <td><Badge bg="secondary">{record.recordState}</Badge></td>
                          <td>{record.metadataTierCurrent}/{record.contentTierCurrent}</td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              {blockers.slice(0, 2).map((blocker) => (
                                <span key={blocker} className="small">{formatBlockerCode(blocker)}</span>
                              ))}
                              {blockers.length > 2 ? <span className="small text-white-50">+{blockers.length - 2} more</span> : null}
                            </div>
                          </td>
                          <td className="text-end">
                            <Button
                              variant="light"
                              size="sm"
                              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary} ${workspaceTheme.workspaceButtonSmall}`}
                              onClick={() => navigate(`/admin/edm/artworks/${record.artworkId}`)}
                            >
                              Open EDM Editor
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
